import { chain, get, map } from 'lodash';
import { AGGREGATORS, CHART_AGGREGATORS, CHART_TYPE, DS_ID } from 'src/constants/chart';

export default function dsBehaviorIncidentDataFlat() {
  return {
    id: DS_ID.BEHAVIOR_INCIDENT,
    label: this.$t('insights.chartConfigurator.sourcesBehaviorIncidentsLabel'),
    hasDateRange: true,

    // True when program type does not have to be loaded separately for filtering
    dataContainsProgramType: true,
    dataContainsTargetStatus: true,

    availableFilters: ['topography'],

    groupByOptions: [
      {
        label: this.$t('insights.chartConfigurator.groupByNoGroupLabel'),
        value: null,
        seriesLabelFrom: null,
      },
      {
        seriesLabelFrom: null,

        // expand: async (dataSeriesSettings) => {
        expand: async () => {
          return map(this.programBehaviourGroups, (group) => ({
            label: group.label,
            value: `bg_${group.id}`,
            seriesLabelFrom: 'behaviourItemLabel',
          }));
        },
      },
    ],

    xAxes: [
      {
        id: 'xaDateDaily',
        label: this.$t('insights.chartConfigurator.xAxisDateDailyLabel'),
        chartType: CHART_TYPE.LINE,
        xAxisType: 'date',
        xAxisAggregateBy: 'incidentId',
        xAxisDataProperty: 'date',
        xAxisCategoryLabel: null,
        xAxisTimeUnit: 'day',
        xAxisOrderBy: 'date',
        xAxisSplitBy: null,
        disableChartTypes: [CHART_TYPE.SCATTERCHART],
      },
      {
        id: 'xaDateWeekly',
        chartType: CHART_TYPE.LINE,
        label: this.$t('insights.chartConfigurator.xAxisDateWeeklyLabel'),
        xAxisType: 'date',
        xAxisAggregateBy: 'incidentId',
        xAxisDataProperty: 'date',
        xAxisCategoryLabel: null,
        xAxisTimeUnit: 'week',
        xAxisOrderBy: 'date',
        xAxisSplitBy: null,
        disableChartTypes: [CHART_TYPE.SCATTERCHART],
      },
      {
        id: 'xaDateMonthly',
        chartType: CHART_TYPE.COLUMN,
        label: this.$t('insights.chartConfigurator.xAxisDateMonthlyLabel'),
        xAxisType: 'date',
        xAxisAggregateBy: 'incidentId',
        xAxisDataProperty: 'date',
        xAxisCategoryLabel: null,
        xAxisTimeUnit: 'month',
        xAxisOrderBy: 'date',
        xAxisSplitBy: null,
        disableChartTypes: [CHART_TYPE.SCATTERCHART],
      },
    ],

    seriesPresets: [
      {
        id: 'spOccurrences',

        label: this.$t('insights.chartConfigurator.seriesOccurrences'),
        dataProperty: 'observationDuration',
        aggregator: AGGREGATORS.COUNT,
        chartAggregator: CHART_AGGREGATORS.SUM,
        chartType: CHART_TYPE.COLUMN,
        axis: 'number',
        axisCategory: null,
        axisCategoryLabel: null,

        // If one row can contain multiple data for series
        arrayGrouping: true,
      },
      {
        id: 'spDurationGroup',
        label: this.$t('insights.chartConfigurator.seriesDuration'),
        isGroupHeader: true,
      },
      {
        id: 'spTotalDuration',

        label: this.$t('measurementMetrics.totalDuration.label'),
        dataProperty: 'observationDuration',
        aggregator: AGGREGATORS.SUM,
        chartAggregator: CHART_AGGREGATORS.SUM,
        chartType: CHART_TYPE.LINE,
        axis: 'duration',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],

        // If one row can contain multiple data for series
        arrayGrouping: true,
      },
      {
        id: 'spMinDuration',

        label: this.$t('measurementMetrics.minDuration.label'),
        dataProperty: 'observationDuration',
        aggregator: AGGREGATORS.LOW,
        chartAggregator: CHART_AGGREGATORS.LOW,
        chartType: CHART_TYPE.LINE,
        axis: 'duration',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],

        // If one row can contain multiple data for series
        arrayGrouping: true,
      },
      {
        id: 'spMaxDuration',

        label: this.$t('measurementMetrics.maxDuration.label'),
        dataProperty: 'observationDuration',
        aggregator: AGGREGATORS.HIGH,
        chartAggregator: CHART_AGGREGATORS.HIGH,
        chartType: CHART_TYPE.LINE,
        axis: 'duration',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],

        // If one row can contain multiple data for series
        arrayGrouping: true,
      },
      {
        id: 'spAvgDuration',

        label: this.$t('measurementMetrics.avgDuration.label'),
        dataProperty: 'observationDuration',
        aggregator: AGGREGATORS.AVERAGE,
        chartAggregator: CHART_AGGREGATORS.AVERAGE,
        chartType: CHART_TYPE.LINE,
        axis: 'duration',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],

        // If one row can contain multiple data for series
        arrayGrouping: true,
      },
    ],

    tableColumns: [
      {
        name: 'date',
        field: 'date',
        format: (val) => (val ? `${this.getLocaleDateString(val)} ${this.getLocaleTimeString(val)}` : ''),
        label: this.$t('insights.chartConfigurator.columnsDate'),
        align: 'center',
        sortable: true,
        visibility: ['incidentId'],
        visibilityExport: ['incidentId'],
      },
      {
        name: 'topography',
        field: 'topography',
        label: this.$t('insights.chartConfigurator.columnsTopography'),
        align: 'left',
        classes: 'insights-truncate-label',
        sortable: false,
        useBadges: true,
        visibility: [null, 'incidentId'],
        visibilityExport: ['incidentId'],

        expand: () => map(this.programBehaviourGroups, (programBehaviourGroup) => ({
          name: `bg_${programBehaviourGroup.id}`,
          field: `bg_${programBehaviourGroup.id}`,
          label: programBehaviourGroup.label,
          format: (items) => chain(items)
            .map((item) => get(this.programBehaviourGroupItems, [programBehaviourGroup.id, item]))
            .compact()
            .value(),
          exportFormat: (items) => {
            return chain(items)
              .map((item) => get(this.programBehaviourGroupItems, [programBehaviourGroup.id, item, 'label']))
              .join(', ')
              .value();
          },
        })),
      },
      {
        name: 'notes',
        field: 'notes',
        label: this.$t('insights.chartConfigurator.columnsNotes'),
        align: 'left',
        classes: 'insights-truncate-label',
        sortable: true,
        visibility: [null, 'incidentId'],
        visibilityExport: [null, 'incidentId'],
      },
    ],
  };
}
